import { Button, SlideDrawer, pantryColors, pantryTypography } from '@pantry/ui'
import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getLoyaltyCampaignHistory } from '../../../../gateways/breadstack/loyaltyMarketingApis'
import {
  type CampaignDisplayStatus,
  type CampaignStats,
  type LoyaltyCampaign,
  type LoyaltyFlow,
  displayStatusForCampaign,
  displayStatusForFlow,
  flowTriggerLabel,
} from '../../../../types/loyaltyMarketing'
import type { LoyaltySegment } from '../../../../types/loyaltySegments'
import { segmentColorOrFallback } from '../../segments/components/SegmentCard'
import { NoteBanner } from '../builder/steps/stepUi'
import {
  type RateCell,
  SMART_SEND_LABEL,
  campaignAudienceSummary,
  campaignChannel,
  audienceFilterLabels,
  campaignType,
  createdByFromHistory,
  formatListDate,
  emailRates,
  engagementRates,
  flowListType,
  isSmartSendCampaign,
  revenueFromStats,
  formatDelay,
} from '../campaignDisplay'
import { SMS_UNAVAILABLE_NOTE, isSmsLaunchBlocked } from '../featureFlags'
import { CampaignActivityList } from './CampaignActivityList'
import {
  CAMPAIGN_TYPE_TINTS,
  CampaignTypeIcon,
  EMERALD,
  flowVisual,
} from './CampaignTypeIcon'
import { FlowActivityList } from './FlowActivityList'
import { StatusPill } from './StatusPill'
import { type ListRowType, TYPE_TAG_TINTS, TypeTag } from './TypeTag'

const p = pantryColors
const F = pantryTypography.fontFamily

export type DrawerTarget =
  | { kind: 'campaign'; campaign: LoyaltyCampaign }
  | { kind: 'flow'; flow: LoyaltyFlow }

type DrawerTab = 'overview' | 'segment' | 'performance' | 'activity'

const TABS: { key: DrawerTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'segment', label: 'Segment' },
  { key: 'performance', label: 'Performance' },
  { key: 'activity', label: 'Activity' },
]

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const formatDateTime = (value?: string): string => formatListDate(value, true)

// One flat view-model so every tab reads the same fields for a campaign or a
// flow (mockup rows: `c.auto` = flow).
interface DrawerModel {
  id: string
  name: string
  subject: string
  previewText: string
  rowType: ListRowType
  status: CampaignDisplayStatus
  statusNote: string
  isAuto: boolean
  stats: CampaignStats | undefined
  channel: 'Email' | 'SMS'
  audienceLabel: string
  audienceSize: string
  segments: LoyaltySegment[]
  dateRow: { label: string; value: string; color?: string } | null
  automationRow: string
  filterLabels: string[]
  // Flow visual (icon + tint), null for campaigns.
  flowVisual: ReturnType<typeof flowVisual> | null
  canPause: boolean
  canResume: boolean
  // Flow draft → live reads "Set live"; a paused flow keeps "Resume".
  canSetLive: boolean
  canSend: boolean
  canSchedule: boolean
  // SMS kill switch is off: launch actions are hidden and a note is shown.
  smsUnavailable: boolean
  canBackToDraft: boolean
  canDelete: boolean
}

const buildModel = (
  target: DrawerTarget,
  segments: LoyaltySegment[],
): DrawerModel => {
  const byId = (id: string) => segments.find((s) => s.id === id)
  if (target.kind === 'campaign') {
    const c = target.campaign
    const status = displayStatusForCampaign(c)
    const audience = campaignAudienceSummary(c.audience, byId)
    const ids = c.audience?.segment_ids?.length
      ? c.audience.segment_ids
      : c.audience?.segment_id
        ? [c.audience.segment_id]
        : []
    return {
      id: c.id,
      name: c.name || 'Campaign',
      subject:
        campaignChannel(c) === 'sms' ? c.sms_body || '' : c.subject || '',
      previewText: c.preview_text || '',
      rowType: campaignType(c),
      status,
      statusNote: c.status_note ?? '',
      isAuto: false,
      stats: c.stats,
      channel: campaignChannel(c) === 'sms' ? 'SMS' : 'Email',
      audienceLabel: audience.label,
      audienceSize:
        audience.size ?? (c.audience?.type === 'ids' ? '' : 'All customers'),
      segments: ids.map(byId).filter((s): s is LoyaltySegment => Boolean(s)),
      dateRow:
        // Smart send time has no single timestamp — each recipient gets their
        // own hour within 24h of launch — so it labels itself instead.
        isSmartSendCampaign(c) &&
        (c.status === 'scheduled' ||
          c.status === 'paused' ||
          c.status === 'preparing' ||
          c.status === 'sending')
          ? { label: 'Send time', value: SMART_SEND_LABEL }
          : c.status === 'scheduled' ||
              c.status === 'paused' ||
              (c.status === 'preparing' && c.scheduled_at)
            ? { label: 'Scheduled for', value: formatDateTime(c.scheduled_at) }
            : c.status === 'draft'
              ? {
                  label: 'Status',
                  value: 'Not yet scheduled',
                  color: p.gray[400],
                }
              : c.sent_at || c.scheduled_at
                ? {
                    label: 'Sent at',
                    value: formatDateTime(c.sent_at || c.scheduled_at),
                  }
                : null,
      automationRow: 'No — manual send',
      filterLabels: audienceFilterLabels(c.audience, campaignChannel(c)),
      flowVisual: null,
      // A Smart send time blast stays 'sending' for up to 24h while its
      // hourly buckets drain, and the server's `sending -> paused` branch
      // exists precisely so that can be stopped — every other 'sending'
      // campaign is a tight loop with nothing left to cancel.
      canPause:
        c.status === 'scheduled' ||
        (c.status === 'sending' && isSmartSendCampaign(c)),
      canResume:
        c.status === 'paused' && !isSmsLaunchBlocked(campaignChannel(c)),
      canSetLive: false,
      canSend: c.status === 'draft' && !isSmsLaunchBlocked(campaignChannel(c)),
      canSchedule:
        c.status === 'draft' && !isSmsLaunchBlocked(campaignChannel(c)),
      smsUnavailable: isSmsLaunchBlocked(campaignChannel(c)),
      canBackToDraft: c.status === 'scheduled' || c.status === 'paused',
      // The server allows delete for any status except an in-flight send.
      canDelete: c.status !== 'sending' && c.status !== 'preparing',
    }
  }
  const f = target.flow
  const status = displayStatusForFlow(f)
  return {
    id: f.id,
    name: f.name || 'Flow',
    subject: f.channel === 'sms' ? f.sms_body || '' : f.subject || '',
    previewText: f.preview_text || '',
    rowType: flowListType(f),
    status,
    statusNote: '',
    isAuto: true,
    stats: f.stats,
    channel: f.channel === 'sms' ? 'SMS' : 'Email',
    audienceLabel: 'All customers',
    audienceSize: 'Auto-targeted',
    segments: [],
    dateRow: f.created_at
      ? { label: 'Automation started', value: formatDateTime(f.created_at) }
      : null,
    filterLabels: audienceFilterLabels(undefined, f.channel ?? 'email').slice(
      1,
    ),
    flowVisual: flowVisual(flowListType(f), f.trigger_metric, 22),
    automationRow: `Yes — ${flowTriggerLabel(f.trigger_metric)}${
      f.delay_value > 0 ? ` +${formatDelay(f.delay_value, f.delay_unit)}` : ''
    }${
      f.trigger_metric === 'abandoned_cart' && (f.min_cart_value ?? 0) > 0
        ? ` · carts over $${f.min_cart_value}`
        : ''
    }`,
    canPause: f.status === 'live',
    canResume: f.status === 'paused' && !isSmsLaunchBlocked(f.channel),
    canSetLive: f.status === 'draft' && !isSmsLaunchBlocked(f.channel),
    smsUnavailable: isSmsLaunchBlocked(f.channel),
    canSend: false,
    canSchedule: false,
    canBackToDraft: f.status === 'live' || f.status === 'paused',
    canDelete: true,
  }
}

// ── Small pieces (mockup `.d-sec`, `.d-row`, `.d-metric`, `.perf-metric`) ──

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div style={{ padding: '18px 0', borderBottom: `1px solid ${p.gray[100]}` }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: p.gray[400],
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginBottom: 12,
      }}
    >
      {title}
    </div>
    {children}
  </div>
)

const Row: React.FC<{
  label: string
  value: React.ReactNode
  color?: string
}> = ({ label, value, color }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      marginBottom: 9,
      fontSize: 13,
    }}
  >
    <div style={{ color: p.gray[500] }}>{label}</div>
    <div
      style={{
        fontWeight: 500,
        color: color ?? p.gray[800],
        textAlign: 'right',
      }}
    >
      {value}
    </div>
  </div>
)

const Metric: React.FC<{
  value: string
  label: string
  color?: string
  last?: boolean
}> = ({ value, label, color, last }) => (
  <div
    style={{
      padding: '13px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      borderRight: last ? 'none' : `1px solid ${p.gray[200]}`,
    }}
  >
    <div style={{ fontSize: 18, fontWeight: 700, color: color ?? p.gray[800] }}>
      {value}
    </div>
    <div
      style={{
        fontSize: 10,
        color: p.gray[400],
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </div>
  </div>
)

const MetricBar: React.FC<{
  label: string
  cell: RateCell
  fill: number
  color: string
}> = ({ label, cell, fill, color }) => {
  const rate = cell.pct
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: 13,
        }}
      >
        <span style={{ color: p.gray[500] }}>{label}</span>
        <span style={{ fontWeight: 700, color: p.gray[800], fontSize: 14 }}>
          {cell.label}
        </span>
      </div>
      <div
        style={{
          height: 7,
          background: p.gray[100],
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            borderRadius: 4,
            width: `${rate === null ? 0 : Math.min(fill, 100)}%`,
            background: color,
          }}
        />
      </div>
    </div>
  )
}

const PauseIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
)

const PlayIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <polygon points="6 4 20 12 6 20 6 4" />
  </svg>
)

const CopyIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
)

const SendIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)

const TrashIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    viewBox="0 0 24 24"
    aria-hidden
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
  </svg>
)

const previewLabelStyle: React.CSSProperties = {
  fontSize: 11,
  color: p.gray[400],
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

// ── Drawer ──────────────────────────────────────────────────────────────────

export const CampaignDetailDrawer: React.FC<{
  storeId: string
  target: DrawerTarget | null
  segments: LoyaltySegment[]
  busy: boolean
  onClose: () => void
  onEdit: (target: DrawerTarget) => void
  onDuplicate: (target: DrawerTarget) => void
  onPause: (target: DrawerTarget) => void
  // Resume for a paused campaign / flow; "Set live" for a draft flow.
  onResume: (target: DrawerTarget) => void
  onSend: (target: DrawerTarget) => void
  onSchedule: (target: DrawerTarget) => void
  onBackToDraft: (target: DrawerTarget) => void
  onDelete: (target: DrawerTarget) => void
}> = ({
  storeId,
  target,
  segments,
  busy,
  onClose,
  onEdit,
  onDuplicate,
  onPause,
  onResume,
  onSend,
  onSchedule,
  onBackToDraft,
  onDelete,
}) => {
  const [tab, setTab] = useState<DrawerTab>('overview')
  const targetId = target
    ? target.kind === 'campaign'
      ? target.campaign.id
      : target.flow.id
    : null
  // Every open starts on Overview (mockup openDrawer()).
  useEffect(() => {
    setTab('overview')
  }, [targetId])

  const model = target ? buildModel(target, segments) : null
  const rates = engagementRates(model?.stats)
  const cells = emailRates(model?.stats)
  const revenue = revenueFromStats(model?.stats)
  const iconType =
    model && !model.flowVisual && model.rowType !== 'flow'
      ? model.rowType
      : null
  const tint = model?.flowVisual
    ? model.flowVisual.tint
    : iconType
      ? CAMPAIGN_TYPE_TINTS[iconType]
      : null
  // "Created by": the campaign has no creator field, so walk the history feed
  // (newest first; `created` is the oldest event) until it turns up — capped
  // at 5 pages, only while the Overview tab is open. Own query key so the
  // Activity list's infinite-query cache is untouched. Omitted if not found.
  const createdByQuery = useQuery({
    queryKey: [
      'loyalty',
      'campaign-created-by',
      storeId,
      target?.kind === 'campaign' ? target.campaign.id : '',
    ],
    queryFn: async (): Promise<string | null> => {
      if (target?.kind !== 'campaign') return null
      let before: string | undefined
      for (let page = 0; page < 5; page += 1) {
        const res = await getLoyaltyCampaignHistory(
          storeId,
          target.campaign.id,
          {
            limit: 50,
            ...(before ? { before } : {}),
          },
        )
        const found = createdByFromHistory(res.events)
        if (found) return found
        if (!res.next_cursor) return null
        before = res.next_cursor
      }
      return null
    },
    enabled:
      target?.kind === 'campaign' && Boolean(storeId) && tab === 'overview',
    staleTime: 5 * 60 * 1000,
  })
  const createdBy = createdByQuery.data ?? undefined

  const renderTab = () => {
    if (!model || !target) return null
    if (tab === 'overview') {
      return (
        <>
          <Section title="Campaign Details">
            <Row label="Type" value={<TypeTag type={model.rowType} />} />
            <Row
              label="Automation"
              value={
                model.isAuto ? (
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      padding: '2px 10px',
                      borderRadius: 20,
                      background: p.purple[50],
                      color: p.purple[700],
                    }}
                  >
                    Yes — auto-triggered
                  </span>
                ) : (
                  'No — manual send'
                )
              }
            />
            {model.isAuto && (
              <Row
                label="Trigger"
                value={model.automationRow.replace(/^Yes — /, '')}
              />
            )}
            {model.dateRow && (
              <Row
                label={model.dateRow.label}
                value={model.dateRow.value}
                color={model.dateRow.color}
              />
            )}
            {createdBy && <Row label="Created by" value={createdBy} />}
            <Row label="Channel" value={model.channel} />
          </Section>
          <Section title="Message Preview">
            <div
              style={{
                background: p.gray[50],
                border: `1px solid ${p.gray[200]}`,
                borderRadius: 10,
                padding: '14px 16px',
              }}
            >
              <div style={{ ...previewLabelStyle, marginBottom: 6 }}>
                {model.channel === 'SMS' ? 'SMS Message' : 'Subject Line'}
              </div>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: p.gray[800],
                  marginBottom: 8,
                  overflowWrap: 'anywhere',
                }}
              >
                {model.subject || '—'}
              </div>
              {model.channel === 'Email' && (
                <>
                  <div style={{ ...previewLabelStyle, marginBottom: 4 }}>
                    Preview Text
                  </div>
                  <div style={{ fontSize: 13, color: p.gray[600] }}>
                    {model.previewText || '—'}
                  </div>
                </>
              )}
            </div>
          </Section>
        </>
      )
    }
    if (tab === 'segment') {
      return (
        <>
          <Section title="Targeting">
            <Row label="Segment" value={model.audienceLabel} />
            <Row
              label="Audience size"
              value={
                model.isAuto
                  ? 'Dynamic (auto-targeted)'
                  : model.audienceSize || '—'
              }
            />
            <Row label="Channel" value={model.channel} />
          </Section>
          <Section title="Filters Applied">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {model.filterLabels.map((label) => (
                <span
                  key={label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    background: p.gray[50],
                    border: `1px solid ${p.gray[200]}`,
                    borderRadius: 6,
                    fontSize: 12.5,
                    color: p.gray[700],
                  }}
                >
                  <svg
                    width={13}
                    height={13}
                    fill="none"
                    stroke={EMERALD}
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                  {label}
                </span>
              ))}
            </div>
          </Section>
          {model.segments.length > 0 && (
            <Section title="Selected Segments">
              {model.segments.map((segment) => (
                <div
                  key={segment.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: p.gray[50],
                    border: `1px solid ${p.gray[200]}`,
                    borderRadius: 6,
                    fontSize: 12.5,
                    color: p.gray[700],
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: segmentColorOrFallback(segment.color),
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, fontWeight: 600 }}>
                    {segment.name}
                  </span>
                  <span style={{ color: p.gray[500] }}>
                    {segment.member_count.toLocaleString()} recipients
                  </span>
                </div>
              ))}
            </Section>
          )}
        </>
      )
    }
    if (tab === 'performance') {
      if (rates.sent === 0) {
        return (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: p.gray[400],
              fontSize: 13.5,
            }}
          >
            {model.isAuto
              ? "No performance data yet — the automation hasn't triggered."
              : "No performance data yet — campaign hasn't been sent."}
          </div>
        )
      }
      const isSmsRow = model.channel === 'SMS'
      return (
        <>
          <Section title="Delivery & Engagement">
            <MetricBar
              label="Delivery Rate"
              cell={cells.delivered}
              fill={cells.delivered.pct ?? 0}
              color={p.blue[700]}
            />
            {!isSmsRow && (
              <>
                <MetricBar
                  label="Open Rate"
                  cell={cells.opens}
                  fill={cells.opens.pct ?? 0}
                  color={p.blue[700]}
                />
                <MetricBar
                  label="Click Rate"
                  cell={cells.clicks}
                  fill={cells.clicks.pct ?? 0}
                  color={EMERALD}
                />
              </>
            )}
            <MetricBar
              label="Conversion Rate"
              cell={cells.conversions}
              fill={(cells.conversions.pct ?? 0) * 8}
              color={p.orange[700]}
            />
          </Section>
          <Section title="Summary Totals">
            <Row
              label={model.isAuto ? 'Times triggered' : 'Sent'}
              value={rates.sent.toLocaleString()}
            />
            <Row label="Delivered" value={rates.delivered.toLocaleString()} />
            {!isSmsRow && (
              <Row label="Opened" value={rates.opens.toLocaleString()} />
            )}
            {!isSmsRow && (
              <Row label="Clicked" value={rates.clicks.toLocaleString()} />
            )}
            <Row
              label="Conversions"
              value={cells.conversions.label}
              color={EMERALD}
            />
            <Row
              label="Revenue"
              value={
                revenue.kind === 'known' && revenue.revenue > 0
                  ? currency.format(revenue.revenue)
                  : '—'
              }
              color={EMERALD}
            />
            <Row
              label="Unsubscribes"
              value={cells.unsubscribes.label}
              color={p.red[700]}
            />
            {(model.stats?.failed ?? 0) > 0 && (
              <Row
                label="Failed"
                value={(model.stats?.failed ?? 0).toLocaleString()}
                color={p.red[700]}
              />
            )}
            {(model.stats?.skipped ?? 0) > 0 && (
              <Row
                label="Skipped"
                value={(model.stats?.skipped ?? 0).toLocaleString()}
                color={p.gray[500]}
              />
            )}
          </Section>
          <div style={{ fontSize: 11.5, color: p.gray[400], lineHeight: 1.5 }}>
            Delivered = accepted by mail provider; opens/clicks are unique per
            recipient; bounces aren't tracked.
          </div>
        </>
      )
    }
    return (
      <Section title={model.isAuto ? 'Trigger Activity' : 'Campaign Activity'}>
        {target.kind === 'campaign' ? (
          <CampaignActivityList
            storeId={storeId}
            campaignId={target.campaign.id}
            enabled={tab === 'activity'}
          />
        ) : (
          <FlowActivityList
            storeId={storeId}
            flowId={target.flow.id}
            enabled={tab === 'activity'}
          />
        )}
      </Section>
    )
  }

  return (
    <SlideDrawer
      open={target !== null}
      onClose={onClose}
      title={model?.name ?? 'Campaign'}
      subtitle={
        model
          ? `${TYPE_TAG_TINTS[model.rowType].label} · ${model.audienceLabel}`
          : 'Campaign details'
      }
      width={520}
      zIndex={1400}
      footer={
        model && target ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {model.canDelete && (
              <Button
                type="button"
                variant="danger-ghost"
                size="sm"
                disabled={busy}
                icon={<TrashIcon />}
                onClick={() => onDelete(target)}
              >
                Delete
              </Button>
            )}
            {model.smsUnavailable && (
              <span style={{ fontSize: 12, color: p.gray[500] }}>
                {SMS_UNAVAILABLE_NOTE}
              </span>
            )}
            {model.canSend && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={busy}
                icon={<SendIcon />}
                onClick={() => onSend(target)}
              >
                Send now
              </Button>
            )}
            {model.canSchedule && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onSchedule(target)}
              >
                Schedule…
              </Button>
            )}
            {(model.canPause || model.canResume || model.canSetLive) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                icon={model.canPause ? <PauseIcon /> : <PlayIcon />}
                onClick={() =>
                  model.canPause ? onPause(target) : onResume(target)
                }
              >
                {model.canPause
                  ? 'Pause'
                  : model.canSetLive
                    ? 'Set live'
                    : 'Resume'}
              </Button>
            )}
            {model.canBackToDraft && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onBackToDraft(target)}
              >
                Back to draft
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              icon={<CopyIcon />}
              onClick={() => onDuplicate(target)}
            >
              Duplicate
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => onEdit(target)}
            >
              {model.isAuto ? 'Edit automation' : 'Edit campaign'}
              <svg
                width={14}
                height={14}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden
                style={{ marginLeft: 4 }}
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Button>
          </div>
        ) : undefined
      }
    >
      {model && tint && (
        <div style={{ fontFamily: F }}>
          {/* Mockup `.d-tab-bar` */}
          <div
            style={{
              display: 'flex',
              borderBottom: `1px solid ${p.gray[200]}`,
              marginBottom: 16,
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background: p.white,
            }}
          >
            {TABS.map((t) => {
              const active = tab === t.key
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  style={{
                    padding: '11px 16px',
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    color: active ? p.brand.primary : p.gray[500],
                    border: 'none',
                    borderBottom: `2px solid ${
                      active ? p.brand.primary : 'transparent'
                    }`,
                    marginBottom: -1,
                    background: 'transparent',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontFamily: F,
                  }}
                >
                  {t.label}
                </button>
              )
            })}
          </div>

          {/* Mockup `.d-hero` */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 14,
              paddingBottom: 16,
              borderBottom: `1px solid ${p.gray[200]}`,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 10,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                background: tint.bg,
                color: tint.fg,
              }}
            >
              {model.flowVisual ? (
                model.flowVisual.icon
              ) : iconType ? (
                <CampaignTypeIcon type={iconType} size={22} />
              ) : null}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: p.gray[800],
                  lineHeight: 1.3,
                }}
              >
                {model.name}
                {model.isAuto && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 10.5,
                      fontWeight: 700,
                      padding: '2px 8px',
                      borderRadius: 20,
                      background: p.purple[50],
                      color: p.purple[700],
                      letterSpacing: '0.03em',
                      verticalAlign: 'middle',
                    }}
                  >
                    AUTOMATION
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: p.gray[500],
                  marginTop: 4,
                  lineHeight: 1.5,
                  overflowWrap: 'anywhere',
                }}
              >
                {model.subject || '—'}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 7,
                  marginTop: 10,
                  flexWrap: 'wrap',
                }}
              >
                <TypeTag type={model.rowType} />
                <StatusPill status={model.status} />
              </div>
              {model.statusNote && (
                <div style={{ marginTop: 10 }}>
                  <NoteBanner>{model.statusNote}</NoteBanner>
                </div>
              )}
            </div>
          </div>

          {/* Mockup `.d-metrics` */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              borderBottom: `1px solid ${p.gray[200]}`,
            }}
          >
            <Metric
              value={rates.sent > 0 ? rates.sent.toLocaleString() : '—'}
              label={model.isAuto ? 'Triggered' : 'Sent'}
            />
            <Metric
              value={
                rates.openRate === null || rates.opens === 0
                  ? '—'
                  : `${rates.openRate}%`
              }
              label="Open Rate"
              color={p.blue[700]}
            />
            <Metric
              value={rates.conversions > 0 ? cells.conversions.label : '—'}
              label="Conversions"
              color={EMERALD}
            />
            <Metric
              value={
                revenue.kind === 'known' && revenue.revenue > 0
                  ? currency.format(revenue.revenue)
                  : '—'
              }
              label="Revenue"
              color={p.orange[700]}
              last
            />
          </div>

          {renderTab()}
        </div>
      )}
    </SlideDrawer>
  )
}
